import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

export interface MarketingReportKpis {
  /** Leads generated in the range (Twenty website-cta). */
  leads: number;
  totalSessions: number;
  engagedSessions: number;
  engagementRate: number;
  uniqueVisitors: number;
  pageviews: number;
}

/** One calendar day. `leads` counts website-cta leads created that day (caller tz). */
export interface MarketingDailyEntry {
  leads: number;
  date: string;
  sessions: number;
  engagedSessions: number;
  pageviews: number;
}

export interface MarketingSourceFunnelRow {
  label: string;
  /** direct | organic | paid | social | referral */
  type: string;
  /** null means "not recorded" for this source, which is not the same as zero. */
  sessions: number | null;
  engagedSessions: number | null;
  contactSessions: number | null;
  leads: number | null;
}

export interface MarketingPage {
  /** Raw GA4 page path, e.g. "/en/pricing" — one row per path, not merged. */
  path: string;
  /** Locale-stripped route, e.g. "/pricing"; equals `path` for the bare form. */
  label: string;
  pageviews: number;
  sessions: number;
  engagedSessions: number;
}

export interface MarketingContactFunnel {
  reached: number;
}

export interface MarketingLead {
  /** Traffic source that produced the lead; "Direct" when captured with no
   *  referrer, "(not set)" when the lead predates attribution capture. */
  trafficSource?: string;
  name: string;
  company: string;
  message: string;
  createdAt: string;
  source: 'website-cta';
  /** Twenty record id, for deep-linking to the person. Null when the note has no
   *  linked person — the row then shows no CRM link at all. */
  crmPersonId: string | null;
}

/**
 * Why GA4 could not be queried (#5480).
 *  - `not_configured`: no Analytics property is configured for the pagemind site (or
 *    more than one matches). Fixed in Settings → Integrations, not by retrying.
 *  - `api_error`: a property was resolved but Google rejected the request.
 */
export type MarketingGa4Reason = 'not_configured' | 'api_error';

export interface MarketingGa4Unavailable {
  available: false;
  reason: MarketingGa4Reason;
  /** The backend's error message, for the banner — never hidden. */
  detail: string;
}

interface MarketingReportBase {
  /** Non-empty when a GA4 widget was truncated: the figures below are still
   *  rendered but under-count, so the UI must say so rather than imply exactness. */
  warnings?: string[];
  range: { start: string; end: string };
  generatedAt: string;
  cache: { hit: boolean; ageSeconds: number };
  /** Twenty CRM leads — present in BOTH shapes; the CRM is independent of GA4. */
  leads: MarketingLead[];
}

/** GA4 answered: every section is present. */
export interface MarketingReportFull extends MarketingReportBase {
  ga4: { available: true };
  kpis: MarketingReportKpis;
  daily: MarketingDailyEntry[];
  sourceFunnel: MarketingSourceFunnelRow[];
  pages: MarketingPage[];
  contactFunnel: MarketingContactFunnel;
}

/**
 * GA4 could not be queried: the CRM side is real, the GA4-derived sections are
 * explicitly null. Null, not zero — zero is a legitimate analytics answer and a
 * widget must never be able to mistake "we could not ask" for it.
 */
export interface MarketingReportCrmOnly extends MarketingReportBase {
  ga4: MarketingGa4Unavailable;
  kpis: null;
  daily: null;
  sourceFunnel: null;
  pages: null;
  contactFunnel: null;
}

export type MarketingReport = MarketingReportFull | MarketingReportCrmOnly;

/** Narrows on the backend's own flag — TypeScript cannot discriminate a union on a
 *  nested property, so the check lives here once rather than as a cast per widget. */
export function isGa4Available(report: MarketingReport): report is MarketingReportFull {
  return report.ga4.available === true;
}

export async function fetchMarketingReport(
  start: string,
  end: string,
  refresh = false,
  signal?: AbortSignal
): Promise<MarketingReport> {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const params = new URLSearchParams({ start, end, tz });
  if (refresh) params.set('refresh', 'true');
  const res = await authFetch(`${API_URL}/analytics/marketing/report?${params}`, { signal });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = (body as any).error || `Request failed: ${res.status}`;
    const detail = (body as any).detail;
    throw new Error(detail ? `${msg} — ${detail}` : msg);
  }
  return assertReportShape(await res.json());
}

/** The GA4-derived sections. Required when `ga4.available` is true, and required to
 *  be explicitly null when it is false. */
const GA4_SECTIONS = ['kpis', 'daily', 'sourceFunnel', 'pages', 'contactFunnel'] as const;
const GA4_REASONS: readonly string[] = ['not_configured', 'api_error'];

/**
 * Fail loudly on a response that doesn't match the contract, rather than letting each
 * widget default a missing section to empty-or-zero.
 *
 * Zero is a legitimate analytics answer, so a widget cannot tell "no leads this month"
 * apart from "this backend predates the leads field" — it would render a confident,
 * wrong dashboard. The realistic cause is a version skew (backend older than this
 * bundle, or a stale cached response), and the only safe response is to say so.
 *
 * The CRM-only shape (#5480) is accepted DELIBERATELY, on the backend's explicit
 * `ga4.available === false` — never by defaulting an absent section. A response with
 * no `ga4` field at all is a backend that predates the flag, and is rejected.
 */
function assertReportShape(body: unknown): MarketingReport {
  const d = body as Record<string, unknown> | null;
  if (!d || typeof d !== 'object') {
    throw new Error(
      'Report response is not an object — every section is missing. The backend is likely ' +
      'older than this dashboard — retry with Refresh, and check the deployed backend version.'
    );
  }
  const missing: string[] = [];
  const contradictions: string[] = [];

  const ga4 = d.ga4 as Record<string, unknown> | null | undefined;
  const available = ga4 && typeof ga4 === 'object' ? ga4.available : undefined;
  if (typeof available !== 'boolean') missing.push('ga4.available');
  if (d.leads == null) missing.push('leads');

  if (available === true) {
    for (const k of GA4_SECTIONS) if (d[k] == null) missing.push(k);
    if (d.kpis != null && (d.kpis as Record<string, unknown>).leads == null) missing.push('kpis.leads');
  } else if (available === false) {
    if (!GA4_REASONS.includes(String(ga4!.reason))) missing.push('ga4.reason');
    if (typeof ga4!.detail !== 'string') missing.push('ga4.detail');
    for (const k of GA4_SECTIONS) {
      // MarketingReportCrmOnly types every section as EXPLICITLY null. An absent key
      // is an incomplete response, not an unavailable section — accepting it would
      // silently upgrade it to the stricter type (codex review, PR #5481).
      if (!Object.prototype.hasOwnProperty.call(d, k)) missing.push(`${k} (must be null)`);
      // The backend says GA4 was NOT queried, so any figure here is a figure it cannot
      // have — rendering it would present an invented number as a measurement.
      else if (d[k] !== null) contradictions.push(k);
    }
  }

  // crmPersonId may legitimately be null (note with no linked person), so this
  // checks the key EXISTS rather than that it's truthy. A backend too old to send
  // it would otherwise render every row as "no CRM link" — indistinguishable from
  // the genuine no-linked-person case, which is exactly the silent fallback the
  // rest of this function exists to prevent.
  if (Array.isArray(d.leads)) {
    const bad = (d.leads as unknown[]).findIndex(
      (l) => !l || typeof l !== 'object' || !Object.prototype.hasOwnProperty.call(l, 'crmPersonId')
    );
    if (bad !== -1) missing.push(`leads[${bad}].crmPersonId`);
  }

  if (contradictions.length > 0) {
    throw new Error(
      `Report response says Google Analytics was unavailable but still carries: ` +
      `${contradictions.join(', ')}. Refusing to render figures the backend claims it did not measure.`
    );
  }
  if (missing.length > 0) {
    throw new Error(
      `Report response is missing: ${missing.join(', ')}. The backend is likely older ` +
      `than this dashboard — retry with Refresh, and check the deployed backend version.`
    );
  }
  return d as unknown as MarketingReport;
}
